/**
 * Navtech Theme — Core JS
 *
 * Handles structural UI only: mobile menu, scroll-to-top, header shadow.
 * GSAP + ScrollTrigger are loaded globally via CDN (inc/enqueue.php)
 * and available for use directly in ACF page templates.
 */

(function () {
  'use strict';

  // requestIdleCallback polyfill
  if (!window.requestIdleCallback) {
    window.requestIdleCallback = (fn) => setTimeout(fn, 1);
  }

  // ── Mobile Menu ──────────────────────────────────────────────────────
  function initMobileMenu() {
    const btn = document.getElementById('mobile-menu-btn');
    const drawer = document.getElementById('mobile-drawer');
    const overlay = document.getElementById('mobile-overlay');
    const close = document.getElementById('mobile-drawer-close');

    if (!btn || !drawer || !overlay || !close) return;

    function open() {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      btn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        drawer.querySelector('a, button:not(#mobile-drawer-close)')?.focus();
      }, 320);
    }

    function closeFn() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      btn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      btn.focus();
    }

    btn.addEventListener('click', open);
    close.addEventListener('click', closeFn);
    overlay.addEventListener('click', closeFn);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeFn();
    });
    window.matchMedia('(min-width: 1024px)').addEventListener('change', (e) => {
      if (e.matches) closeFn();
    });
  }

  // ── Scroll-to-top ────────────────────────────────────────────────────
  function initScrollTop() {
    const btn = document.getElementById('scroll-top');
    if (!btn) return;

    window.addEventListener(
      'scroll',
      () => {
        const show = window.scrollY > 400;
        btn.classList.toggle('opacity-0', !show);
        btn.classList.toggle('pointer-events-none', !show);
        btn.classList.toggle('opacity-100', show);
      },
      { passive: true }
    );

    btn.addEventListener('click', () =>
      window.scrollTo({ top: 0, behavior: 'smooth' })
    );
  }

  // ── Header: auto-hide on scroll down, reveal on scroll up ────────────
  function initHeaderAutoHide() {
    const header = document.getElementById('site-header');
    if (!header) return;

    let lastY = 0;
    let ticking = false;
    const DELTA = 5;

    function update() {
      const y = window.pageYOffset;
      const dy = y - lastY;

      header.classList.toggle('is-scrolled', y > 10);
      header.classList.toggle('shadow-md', y > 10);

      if (Math.abs(dy) < DELTA) {
        ticking = false;
        return;
      }

      lastY = y;
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );

  }

  // ── Smooth anchor scroll ─────────────────────────────────────────────
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach((link) => {
      link.addEventListener('click', function (e) {
        const id = this.getAttribute('href').slice(1);
        if (!id) return;
        const target = document.getElementById(id);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          if (!target.hasAttribute('tabindex'))
            target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        }
      });
    });
  }

  // ── Mega-menu dropdowns ──────────────────────────────────────────────
  function initMegaMenu() {
    const items = document.querySelectorAll('.nt-nav-item');
    if (!items.length) return;

    let openMenu = null;
    let closeTimer = null;
    const DELAY = 120;
    const INDUSTRIES_CLOSE_DELAY = 150;
    const CLOSE_ANIMATION_DURATION = 200;

    function positionArrow(li, mega) {
      const link = li.querySelector('.nt-nav-link');
      const arrow = mega.querySelector('.nt-mega__arrow');
      if (!link || !arrow) return;

      const linkRect = link.getBoundingClientRect();
      const megaRect = mega.getBoundingClientRect();
      const offset = linkRect.left + linkRect.width / 2 - megaRect.left - 8;
      arrow.style.setProperty('--arrow-x', offset + 'px');
      arrow.style.marginLeft = '0';
      arrow.style.marginRight = '0';
      arrow.style.position = 'relative';
      arrow.style.left = offset + 'px';
    }

    function openMega(li) {
      clearTimeout(closeTimer);
      if (openMenu && openMenu !== li) closeMega(openMenu);

      const mega = li.querySelector('.nt-mega');
      const link = li.querySelector('.nt-nav-link');
      const chevron = li.querySelector('.nt-chevron');
      if (!mega) return;

      const header = document.getElementById('site-header');
      if (header) {
        mega.style.top = header.offsetHeight + 'px';
        mega.style.setProperty('--nt-mega-top', header.offsetHeight + 'px');
        if (li.classList.contains('nt-nav-item--industries')) {
          header.classList.add('is-industries-open');
        }
      }

      mega.classList.remove('hidden');
      requestAnimationFrame(() => {
        mega.classList.add('is-open');
        positionArrow(li, mega);
      });
      link?.setAttribute('aria-expanded', 'true');
      chevron?.classList.add('is-flipped');
      openMenu = li;
    }

    function closeMega(li) {
      const mega = li.querySelector('.nt-mega');
      const link = li.querySelector('.nt-nav-link');
      const chevron = li.querySelector('.nt-chevron');
      if (!mega) return;

      mega.classList.remove('is-open');
      link?.setAttribute('aria-expanded', 'false');
      chevron?.classList.remove('is-flipped');
      if (li.classList.contains('nt-nav-item--industries')) {
        document.getElementById('site-header')?.classList.remove('is-industries-open');
      }

      setTimeout(() => {
        if (!mega.classList.contains('is-open')) {
          mega.classList.add('hidden');
        }
      }, CLOSE_ANIMATION_DURATION);

      if (openMenu === li) openMenu = null;
    }

    function scheduleClose(li) {
      const delay = li.querySelector('.nt-mega--industries') ? INDUSTRIES_CLOSE_DELAY : DELAY;
      closeTimer = setTimeout(() => closeMega(li), delay);
    }

    items.forEach((li) => {
      const mega = li.querySelector('.nt-mega');
      if (!mega) return;

      li.addEventListener('mouseenter', () => openMega(li));
      li.addEventListener('mouseleave', () => scheduleClose(li));
      mega.addEventListener('mouseenter', () => clearTimeout(closeTimer));
      mega.addEventListener('mouseleave', () => scheduleClose(li));

      const link = li.querySelector('.nt-nav-link');
      if (link) {
        link.addEventListener('click', (e) => {
          if (li.querySelector('.nt-mega')) {
            e.preventDefault();
            if (mega.classList.contains('is-open')) {
              closeMega(li);
            } else {
              openMega(li);
            }
          }
        });

        link.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (mega.classList.contains('is-open')) {
              closeMega(li);
            } else {
              openMega(li);
            }
          }
          if (e.key === 'Escape' && mega.classList.contains('is-open')) {
            closeMega(li);
            link.focus();
          }
        });
      }
    });

    document.addEventListener('click', (e) => {
      if (openMenu && !openMenu.contains(e.target)) {
        closeMega(openMenu);
      }
    });
  }

  // ── Mobile nav accordion for mega-menu structure ─────────────────────
  function initMobileNavAccordion() {
    const parents = document.querySelectorAll(
      '#mobile-drawer .nt-mobile-parent'
    );
    if (!parents.length) return;

    parents.forEach((btn) => {
      const chevron = btn.querySelector('.nt-mobile-chevron');
      const sub = btn.nextElementSibling;
      if (!sub || !sub.classList.contains('nt-mobile-sub')) return;

      let closeTimer;

      sub.addEventListener('transitionend', (event) => {
        if (event.propertyName === 'max-height' && btn.getAttribute('aria-expanded') === 'true') {
          sub.style.maxHeight = 'none';
        }
      });

      btn.addEventListener('click', () => {
        const isOpen = btn.getAttribute('aria-expanded') === 'true';
        clearTimeout(closeTimer);

        if (isOpen) {
          btn.setAttribute('aria-expanded', 'false');
          sub.setAttribute('aria-hidden', 'true');
          chevron?.classList.remove('is-expanded');
          sub.style.maxHeight = `${sub.scrollHeight}px`;
          requestAnimationFrame(() => {
            sub.style.maxHeight = '0px';
            sub.style.opacity = '0';
          });
          closeTimer = setTimeout(() => {
            if (btn.getAttribute('aria-expanded') === 'false') {
              sub.classList.add('hidden');
            }
          }, 300);
        } else {
          sub.classList.remove('hidden');
          sub.style.maxHeight = '0px';
          sub.style.opacity = '0';
          sub.setAttribute('aria-hidden', 'false');
          btn.setAttribute('aria-expanded', 'true');
          chevron?.classList.add('is-expanded');
          requestAnimationFrame(() => {
            sub.style.maxHeight = `${sub.scrollHeight}px`;
            sub.style.opacity = '1';
          });
        }
      });
    });
  }

  // ── .btn-primary / .btn-outline: position-aware circle hover (inject span, set position) ─
  function initBtnCircleHover() {
    const selector = '.btn-primary, .btn-secondary, .btn-outline';
    document.querySelectorAll(selector).forEach((el) => {
      if (!el.querySelector('.btn-primary__label')) {
        const label = document.createElement('span');
        label.className = 'btn-primary__label';
        while (el.firstChild) {
          label.appendChild(el.firstChild);
        }
        el.appendChild(label);
      }
      if (!el.querySelector('.btn-primary__circle')) {
        const circle = document.createElement('span');
        circle.className = 'btn-primary__circle';
        el.appendChild(circle);
      }
    });

    document.querySelectorAll(selector).forEach((btn) => {
      const circle = btn.querySelector('.btn-primary__circle');
      if (!circle) return;

      function setPosition(e) {
        const rect = btn.getBoundingClientRect();
        const left = e.clientX - rect.left;
        const top = e.clientY - rect.top;
        circle.style.left = left + 'px';
        circle.style.top = top + 'px';
      }

      btn.addEventListener('mouseenter', setPosition);
      btn.addEventListener('mouseout', setPosition);
    });
  }

  // ── Scroll reveal: add .is-visible when element (or stagger parent) enters viewport ─
  function initScrollReveal() {
    const STAGGER_MS = 80;
    const STAGGER_BASE_DELAY_MS = 50;
    const rootMargin = '0px 0px -40px 0px';
    const threshold = 0.08;

    const revealOne = (el) => {
      if (el.dataset.revealed === '1') return;
      el.dataset.revealed = '1';
      el.classList.add('is-visible');
    };

    const standalone = document.querySelectorAll('.scroll-reveal');
    const staggerParents = document.querySelectorAll('.scroll-reveal-stagger');
    const standaloneSet = new Set(
      Array.from(staggerParents).flatMap((p) => Array.from(p.children))
    );

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          if (el.classList.contains('scroll-reveal-stagger')) {
            el.dataset.revealed = '1';
            el.classList.add('is-visible');
            const children = Array.from(el.children);
            children.forEach((child, i) => {
              child.style.transitionDelay =
                STAGGER_BASE_DELAY_MS + i * STAGGER_MS + 'ms';
            });
          } else {
            revealOne(el);
          }
          observer.unobserve(el);
        });
      },
      { rootMargin, threshold }
    );

    standalone.forEach((el) => {
      if (standaloneSet.has(el)) return;
      observer.observe(el);
    });
    staggerParents.forEach((el) => observer.observe(el));
  }

  // ── Experience section: count-up from 0 when stat is in view ────────
  function initExperienceCounters() {
    const els = document.querySelectorAll('.experience-stat-counter');
    if (!els.length) return;

    const duration = 1800;
    const easeOutQuart = (t) => 1 - (1 - t) ** 4;

    const getCounterParts = (el) => {
      const prefix = el.dataset.prefix || '';
      const suffix = el.dataset.suffix || '';
      return { prefix, suffix };
    };

    const runCounter = (el) => {
      const target = parseInt(el.dataset.count, 10);
      const { prefix, suffix } = getCounterParts(el);
      if (!Number.isFinite(target) || target <= 0) return;
      let start = null;
      const step = (ts) => {
        if (!start) start = ts;
        const elapsed = ts - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = easeOutQuart(progress);
        const current = Math.round(eased * target);
        el.textContent = prefix + current + suffix;
        if (progress < 1) requestAnimationFrame(step);
        else el.textContent = prefix + target + suffix;
      };
      requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          if (el.dataset.counted === '1') return;
          el.dataset.counted = '1';
          runCounter(el);
          observer.unobserve(el);
        });
      },
      { rootMargin: '0px 0px -50px 0px', threshold: 0.1 }
    );
    els.forEach((el) => observer.observe(el));
  }

  // ── Case Study: shared particles.js initialiser ───────────────────────
  // particles.js with detect_on:'window' stores raw clientX/Y without
  // subtracting the canvas offset, so particles react at the wrong position
  // when the section is scrolled. We patch with getBoundingClientRect().
  function initParticlesSection(id) {
    var el = document.getElementById(id);
    if (!el || typeof particlesJS === 'undefined') return;

    particlesJS(id, {
      particles: {
        number: { value: 140, density: { enable: true, value_area: 900 } },
        color: { value: '#ffffff' },
        shape: { type: 'circle' },
        opacity: { value: 0.5, random: true },
        size: { value: 2.5, random: true },
        line_linked: {
          enable: true,
          distance: 150,
          color: '#ffffff',
          opacity: 0.35,
          width: 1,
        },
        move: {
          enable: true,
          speed: 1.4,
          direction: 'none',
          random: true,
          straight: false,
          out_mode: 'out',
          bounce: false,
        },
      },
      interactivity: {
        detect_on: 'window',
        events: {
          onhover: { enable: true, mode: 'grab' },
          onclick: { enable: true, mode: 'push' },
          resize: true,
        },
        modes: {
          grab: { distance: 160, line_linked: { opacity: 0.9 } },
          push: { particles_nb: 4 },
        },
      },
      retina_detect: true,
    });

    var pJSInst = window.pJSDom && window.pJSDom[window.pJSDom.length - 1];
    if (pJSInst && pJSInst.pJS) {
      window.addEventListener(
        'mousemove',
        function (e) {
          var rect = el.getBoundingClientRect();
          // particles.js stores positions in physical pixels (CSS px × pxratio),
          // so we must apply the same scaling after subtracting the canvas offset.
          var dpr = pJSInst.pJS.canvas.pxratio || 1;
          pJSInst.pJS.interactivity.mouse.pos_x = (e.clientX - rect.left) * dpr;
          pJSInst.pJS.interactivity.mouse.pos_y = (e.clientY - rect.top) * dpr;
        },
        { passive: true }
      );
    }
  }

  // ── Case Studies page: AJAX search + load-more ───────────────────────
  function initCaseStudiesPage() {
    const grid = document.getElementById('cs-grid');
    const form = document.getElementById('cs-search-form');
    const moreBtn = document.getElementById('cs-load-more');
    if (!grid || !form) return;

    const pageUrl = form.action;
    let csPage = 1;
    let isLoading = false;
    let debounceId = null;

    function getFormParams() {
      const out = {};
      new FormData(form).forEach((val, key) => {
        if (val) out[key] = val;
      });
      return out;
    }

    function setBtnState(loading) {
      if (!moreBtn) return;
      moreBtn.disabled = loading;
      const lbl = moreBtn.querySelector('.btn-primary__label');
      if (lbl) lbl.textContent = loading ? 'Loading\u2026' : 'Load More';
    }

    function fetchCards(params, append) {
      if (isLoading) return;
      isLoading = true;
      setBtnState(true);
      if (!append) grid.style.opacity = '0.5';

      const url = new URL(pageUrl, window.location.origin);
      url.searchParams.set('ajax_cs', '1');
      Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));

      fetch(url.toString(), { credentials: 'same-origin' })
        .then((r) => r.json())
        .then((data) => {
          if (append) {
            const tmp = document.createElement('div');
            tmp.innerHTML = data.html;
            Array.from(tmp.children).forEach((el) => grid.appendChild(el));
          } else {
            grid.innerHTML = data.html;
            csPage = parseInt(params.cs_page || '1', 10);
          }
          if (moreBtn) {
            moreBtn.classList.toggle('hidden', !data.has_more);
          }
        })
        .catch(() => {})
        .finally(() => {
          isLoading = false;
          setBtnState(false);
          if (!append) grid.style.opacity = '1';
        });
    }

    // Search with debounce
    const searchInput = form.querySelector('[name="cs_search"]');
    if (searchInput) {
      searchInput.addEventListener('input', () => {
        clearTimeout(debounceId);
        debounceId = setTimeout(() => {
          csPage = 1;
          fetchCards(getFormParams(), false);
        }, 350);
      });
    }

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      csPage = 1;
      fetchCards(getFormParams(), false);
    });

    // Load More
    if (moreBtn) {
      moreBtn.addEventListener('click', () => {
        if (isLoading) return;
        csPage++;
        fetchCards(
          Object.assign({}, getFormParams(), { cs_page: csPage }),
          true
        );
      });
    }
  }

  // ── Industries We Power Tabs (home page carousel) ───────────────────────
  function initIndustriesPowerTabs() {
    const section = document.querySelector('.ind-power-section');
    if (!section) return;

    const tabBtns = section.querySelectorAll('.ind-power-tab-btn');
    const progressBtns = section.querySelectorAll('.ind-power-progress-btn');
    const panels = section.querySelectorAll('.ind-power-panel');
    const prevBtn = section.querySelector('.ind-power-nav-prev');
    const nextBtn = section.querySelector('.ind-power-nav-next');
    const total = panels.length;
    if (!total) return;
    const AUTO_INTERVAL = 3500;
    const RESUME_DELAY = 8000;

    let current = 0;
    let timer = null;
    let resumeTimer = null;

    function switchTab(index) {
      current = index;
      const idx = String(index);
      tabBtns.forEach(btn => btn.classList.toggle('is-active', btn.dataset.index === idx));
      progressBtns.forEach(btn => btn.classList.toggle('is-active', btn.dataset.index === idx));
      panels.forEach(panel => panel.classList.toggle('is-active', panel.dataset.index === idx));
    }

    function startAuto() {
      clearInterval(timer);
      timer = setInterval(() => {
        current = (current + 1) % total;
        switchTab(current);
      }, AUTO_INTERVAL);
    }

    function pauseAuto() {
      clearInterval(timer);
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(startAuto, RESUME_DELAY);
    }

    tabBtns.forEach(btn => btn.addEventListener('click', () => {
      switchTab(Number(btn.dataset.index));
      pauseAuto();
    }));
    progressBtns.forEach(btn => btn.addEventListener('click', () => {
      switchTab(Number(btn.dataset.index));
      pauseAuto();
    }));
    if (prevBtn) prevBtn.addEventListener('click', () => {
      switchTab((current - 1 + total) % total);
      pauseAuto();
    });
    if (nextBtn) nextBtn.addEventListener('click', () => {
      switchTab((current + 1) % total);
      pauseAuto();
    });

    // Start auto-play only when section enters viewport
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          startAuto();
        } else {
          clearInterval(timer);
          clearTimeout(resumeTimer);
        }
      });
    }, { threshold: 0.3 });
    observer.observe(section);
  }

  // ── Nav CTA: show buttons once hero leaves viewport, hide at footer ──
  function initNavCta() {
    const header = document.getElementById('site-header');
    const navCta = document.getElementById('nav-cta-group');
    if (!header || !navCta) return;

    const heroEl =
      document.querySelector('.home-hero, .navtech-hero, .about-hero') ||
      document.querySelector('#main section');
    if (!heroEl) return;

    let heroPassed = false;
    let footerVisible = false;
    let closingCtaVisible = false;

    function update() {
      const show = heroPassed && !footerVisible && !closingCtaVisible;
      header.classList.toggle('hero-passed', show);
      navCta.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    new IntersectionObserver(
      (entries) => entries.forEach((e) => { heroPassed = !e.isIntersecting; update(); }),
      { threshold: 0 }
    ).observe(heroEl);

    const footerEl = document.getElementById('site-footer');
    if (footerEl) {
      new IntersectionObserver(
        (entries) => entries.forEach((e) => { footerVisible = e.isIntersecting; update(); }),
        { threshold: 0 }
      ).observe(footerEl);
    }

    // Closing CTA banners already repeat these same buttons, so hide the
    // header's floating copy while one is on screen (not just at the footer).
    const closingCtaEls = document.querySelectorAll('.nt-closing-cta');
    if (closingCtaEls.length) {
      const closingCtaObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => { e.target.dataset.ntVisible = e.isIntersecting ? '1' : '0'; });
          closingCtaVisible = Array.from(closingCtaEls).some((el) => el.dataset.ntVisible === '1');
          update();
        },
        { threshold: 0 }
      );
      closingCtaEls.forEach((el) => closingCtaObserver.observe(el));
    }
  }

  // ── Register GSAP plugins once they are available ────────────────────
  function initGSAP() {
    if (typeof gsap === 'undefined') return;
    if (typeof ScrollTrigger !== 'undefined') {
      gsap.registerPlugin(ScrollTrigger);
    }
  }

  // ── Hero quick-pick chips ─────────────────────────────────────────────
  function initHeroChips() {
    const chips = document.querySelectorAll('[data-nt-chip-text]');
    const input = document.getElementById('hero-ai-input');
    if (!chips.length || !input) return;

    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        input.value = chip.dataset.ntChipText;
        input.focus();
        chips.forEach((c) => c.classList.remove('is-active'));
        chip.classList.add('is-active');
      });
    });
  }

  function initMethodologyTimeline() {
    const timeline = document.querySelector('.au-methodology-timeline');
    if (!timeline) return;

    const steps = Array.from(timeline.querySelectorAll('.au-method-step'));
    const STEP_GAP = 480;
    let played = false;

    const lineDur = ((steps.length - 1) * STEP_GAP + 700) / 1000;
    timeline.style.setProperty('--au-line-dur', lineDur.toFixed(2) + 's');

    function playJourney() {
      if (played) return;
      played = true;
      timeline.classList.add('is-playing');
      steps.forEach((step, i) => {
        setTimeout(() => step.classList.add('is-active'), i * STEP_GAP);
      });
    }

    new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          playJourney();
        }
      });
    }, { threshold: 0.15 }).observe(timeline);
  }

  function initTestiVideo() {
    document.querySelectorAll('.testi-video-player').forEach(player => {
      const activate = () => {
        const id    = player.dataset.videoId;
        const embed = player.dataset.embed;
        const src   = id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` : embed;
        if (!src) return;
        const iframe = document.createElement('iframe');
        iframe.src = src;
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.className = 'testi-video-iframe';
        player.replaceChildren(iframe);
      };
      player.addEventListener('click', activate);
      player.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(); }
      });
    });
  }

  function initInsightPage() {
    const siContent = document.querySelector('.insight-column');
    if (siContent) {
      const faqBlock = siContent.querySelector('.faq-block');
      const trackHeight = faqBlock ? faqBlock.offsetTop : siContent.offsetHeight;
      siContent.style.setProperty('--line-track', trackHeight + 'px');

      const updateContentLine = () => {
        const rect = siContent.getBoundingClientRect();
        const scrolled = Math.max(0, -rect.top + window.innerHeight * 0.5);
        const fill = Math.min(trackHeight, scrolled);
        siContent.style.setProperty('--line-fill', fill + 'px');
      };
      window.addEventListener('scroll', updateContentLine, { passive: true });
      updateContentLine();
    }


    document.querySelectorAll('.faq-item-trigger').forEach((btn) => {
      btn.addEventListener('click', function () {
        const item = this.closest('.faq-item');
        const isOpen = item.classList.contains('is-open');
        document.querySelectorAll('.faq-item').forEach((i) => {
          i.classList.remove('is-open');
          i.querySelector('.faq-item-trigger').setAttribute('aria-expanded', 'false');
        });
        if (!isOpen) {
          item.classList.add('is-open');
          this.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  // ── Bootstrap ────────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', () => {
    initMobileMenu();
    initMegaMenu();
    initScrollTop();
    initHeaderAutoHide();
    initSmoothScroll();
    initBtnCircleHover();
    initCaseStudiesPage();
    initScrollReveal();
    initExperienceCounters();
    initParticlesSection('cs-problem-particles');
    initParticlesSection('cs-why-particles');
    initMobileNavAccordion();
    initIndustriesPowerTabs();
    initNavCta();
    initHeroChips();
    initInsightPage();
    initMethodologyTimeline();
    initTestiVideo();
    requestIdleCallback(initGSAP, { timeout: 2000 });
  });
})();
